// Office hours: stored per office as { mon: ['08:00-12:00', '13:00-17:00'], ..., sun: [] }.
// Firestore can't store a list inside a list, so each open window is one 'HH:MM-HH:MM' string.
// Older data used [['08:00','12:00']] tuples; everything here reads both.
// A missing `hours` object means "unknown", which is different from closed.
import { hm, toHM, fmtMin, dowOf, zonedToMs } from './time.js';

export const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const DAY_LABEL = { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' };

const pack = ([a, b]) => `${a}-${b}`;
/** One stored window ('08:00-12:00', ['08:00','12:00'] or {open, close}) as an ['08:00','12:00'] pair. */
function unpack(w) {
  if (typeof w === 'string') { const [a, b] = w.split('-'); return [a, b]; }
  if (Array.isArray(w)) return [w[0], w[1]];
  if (w && typeof w === 'object') return [w.open, w.close];
  return [null, null];
}

/** Hours in the stored shape with every day present, whatever format they were saved in. */
export function normHours(hours) {
  if (!hours) return null;
  return Object.fromEntries(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d, i) => [d, (intervals(hours, (i + 1) % 7) || []).map(pack)]));
}

/** Convert Google Places `regularOpeningHours` into our shape. Returns null when Google has no periods. */
export function fromPlaces(roh) {
  if (!roh || !Array.isArray(roh.periods) || !roh.periods.length) return null;
  const out = Object.fromEntries(DAYS.map((d) => [d, []]));
  for (const p of roh.periods) {
    const o = p.open;
    if (!o || o.day == null) continue;
    // Open 24 hours: a single period with no close.
    if (!p.close) { out[DAYS[o.day]].push(['00:00', '23:59']); continue; }
    const start = (o.hour || 0) * 60 + (o.minute || 0);
    const c = p.close;
    let end = (c.hour || 0) * 60 + (c.minute || 0);
    if (c.day !== o.day) end = 23 * 60 + 59; // runs past midnight; deliveries never matter after that
    if (end > start) out[DAYS[o.day]].push([toHM(start), toHM(end)]);
  }
  for (const d of DAYS) out[d] = out[d].sort((a, b) => hm(a[0]) - hm(b[0])).map(pack);
  return out;
}

/** Parse what dispatch types (returns stored strings) in the hours editor: '8-12, 1-5', '8:00-17:00', 'closed'. */
export function parseHoursText(txt) {
  txt = String(txt || '').trim().toLowerCase();
  if (!txt || txt === 'closed' || txt === '-') return [];
  return txt.split(/[,;]/).map((p) => p.trim()).filter(Boolean)
    .map((p) => p.split(/\s*(?:-|–|to)\s*/))
    .filter(([a, b]) => hm(a) != null && hm(b) != null)
    .map(([a, b]) => {
      let A = hm(a); let B = hm(b);
      // No office opens before 6 AM, so a bare '1' means 1 PM (after a lunch break).
      if (A < 6 * 60 && !/a/.test(a)) A += 12 * 60;
      // '8-5' and '1-5' close in the afternoon.
      if (B <= A && B < 13 * 60) B += 12 * 60;
      return A < B ? [toHM(A), toHM(Math.min(B, 23 * 60 + 59))] : null;
    })
    .filter(Boolean)
    .sort((x, y) => hm(x[0]) - hm(y[0]))
    .map(pack);
}

export function hoursText(iv) {
  if (!iv || !iv.length) return 'Closed';
  return iv.map(([a, b]) => `${fmtMin(hm(a)).replace(':00', '')} to ${fmtMin(hm(b)).replace(':00', '')}`).join(', ');
}

/** Open windows on a weekday as ['08:00','12:00'] pairs. null when hours are unknown. */
export function intervals(hours, dow) {
  if (!hours) return null;
  const v = hours[DAYS[dow]];
  return Array.isArray(v) ? v.map(unpack).filter(([a, b]) => hm(a) != null && hm(b) != null) : [];
}

/**
 * Open/closed status of an office at a minute of the day.
 * s: 'open' | 'soon' (closes within 30 min) | 'closed' | 'unk'
 */
export function statusAt(hours, dow, min) {
  if (!hours) return { s: 'unk', label: 'Hours not set' };
  const iv = intervals(hours, dow);
  for (const [a, b] of iv) {
    const A = hm(a); const B = hm(b);
    if (min >= A && min < B) {
      return B - min <= 30
        ? { s: 'soon', label: `Closes ${fmtMin(B)}`, closeAt: B }
        : { s: 'open', label: `Open until ${fmtMin(B)}`, closeAt: B };
    }
  }
  // Closed at this time: say why, so a driver can tell a lunch break from closed for the day.
  const later = iv.map(([a]) => hm(a)).filter((A) => A > min).sort((x, y) => x - y)[0];
  const ended = iv.map(([, b]) => hm(b)).filter((B) => B <= min).sort((x, y) => y - x)[0];
  if (later != null && ended != null) return { s: 'closed', label: `Closed ${fmtMin(ended)} to ${fmtMin(later)}`, openAt: later, closedAt: ended };
  if (later != null) return { s: 'closed', label: `Not open yet, opens ${fmtMin(later)}`, openAt: later };
  if (ended != null) return { s: 'closed', label: `Closed at ${fmtMin(ended)}`, closedAt: ended };
  return { s: 'closed', label: 'Closed all day' };
}

export function isOpen(hours, dow, min) {
  const s = statusAt(hours, dow, min).s;
  return s === 'open' || s === 'soon' || s === 'unk';
}

/** Open windows on a date as UTC ms pairs, or null when hours are unknown. */
export function windowsOn(hours, dateStr) {
  if (!hours) return null;
  return intervals(hours, dowOf(dateStr)).map(([a, b]) => [zonedToMs(dateStr, a), zonedToMs(dateStr, b)]);
}
